
import React, { ChangeEvent, useState, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Image as ImageIcon, Settings } from "lucide-react";
import ImageUploadOptions from "./ImageUploadOptions";
import ImageCompressionStatus from "./ImageCompressionStatus";
import { 
  compressImage, 
  shouldCompress, 
  getOptimalCompressionSettings,
  type CompressionResult 
} from "@/utils/imageCompression";

interface ImageUploadFieldProps {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const ImageUploadField = ({ onChange }: ImageUploadFieldProps) => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionResult, setCompressionResult] = useState<CompressionResult>();
  const [compressionError, setCompressionError] = useState<string>();
  const [autoCompress, setAutoCompress] = useState(true);
  const [showOptions, setShowOptions] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const createImagePreview = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setCompressionResult(undefined);
    setCompressionError(undefined);
    setShowOptions(true);
    
    // Clear all input values
    [fileInputRef, galleryInputRef, cameraInputRef].forEach(ref => {
      if (ref.current) ref.current.value = '';
    });
    
    // Create a fake event to notify parent of removal
    const fakeEvent = {
      target: { files: null, value: '' },
      currentTarget: null,
      nativeEvent: null,
      preventDefault: () => {},
      stopPropagation: () => {},
      bubbles: false,
      cancelable: false,
      defaultPrevented: false,
      eventPhase: 0,
      isTrusted: false,
      timeStamp: Date.now(),
      type: 'change'
    } as unknown as ChangeEvent<HTMLInputElement>;
    onChange(fakeEvent);
  };

  const processImage = async (file: File) => {
    try {
      setCompressionError(undefined);
      
      // Always create preview first
      createImagePreview(file);
      
      if (autoCompress && shouldCompress(file)) {
        setIsCompressing(true);
        
        const compressionSettings = getOptimalCompressionSettings(file);
        const result = await compressImage(file, compressionSettings);
        
        setCompressionResult(result);
        setSelectedImage(result.file);
        
        // Create event with compressed file
        const fakeEvent = {
          target: { files: [result.file] },
          currentTarget: null,
          nativeEvent: null,
          preventDefault: () => {},
          stopPropagation: () => {},
          bubbles: false,
          cancelable: false,
          defaultPrevented: false,
          eventPhase: 0,
          isTrusted: false,
          timeStamp: Date.now(),
          type: 'change'
        } as unknown as ChangeEvent<HTMLInputElement>;
        onChange(fakeEvent);
        
        toast.success(`Image compressed by ${result.compressionRatio}%`);
      } else {
        setSelectedImage(file);
        
        // Create event with original file
        const fakeEvent = {
          target: { files: [file] },
          currentTarget: null,
          nativeEvent: null,
          preventDefault: () => {},
          stopPropagation: () => {},
          bubbles: false,
          cancelable: false,
          defaultPrevented: false,
          eventPhase: 0,
          isTrusted: false,
          timeStamp: Date.now(),
          type: 'change'
        } as unknown as ChangeEvent<HTMLInputElement>;
        onChange(fakeEvent);
      }
      
      setShowOptions(false);
      setIsCompressing(false);
    } catch (error) {
      setIsCompressing(false);
      setCompressionError(error instanceof Error ? error.message : 'Compression failed');
      
      // Use original file if compression fails
      setSelectedImage(file);
      const fakeEvent = {
        target: { files: [file] },
        currentTarget: null,
        nativeEvent: null,
        preventDefault: () => {},
        stopPropagation: () => {},
        bubbles: false,
        cancelable: false,
        defaultPrevented: false,
        eventPhase: 0,
        isTrusted: false,
        timeStamp: Date.now(),
        type: 'change'
      } as unknown as ChangeEvent<HTMLInputElement>;
      onChange(fakeEvent);
      
      toast.error('Image compression failed, using original');
    }
  };

  const handleGallerySelect = () => {
    if (galleryInputRef.current) {
      galleryInputRef.current.click();
    }
  };

  const handleCameraSelect = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleEitherSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    console.log("ImageUploadField - File selected:", {
      file: file,
      fileName: file?.name,
      fileSize: file?.size,
      fileType: file?.type,
      hasFiles: !!e.target.files?.length
    });

    // Validate file type - Enhanced Android camera compatibility
    if (file) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split('.').pop();
      
      // Enhanced camera detection for better validation
      const isLikelyFromCamera = fileName === 'image.jpg' || fileName === 'image.jpeg' || 
                                fileName === 'image.png' || !file.type || 
                                fileName.includes('camera') || fileName.includes('photo');
      
      // Android PWA might not provide correct MIME type, so also check file extension
      const isValidMimeType = file.type && validTypes.includes(file.type);
      const isValidExtension = fileExtension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
      
      // For camera files, be more lenient with validation
      const isValid = isLikelyFromCamera ? 
        (isValidExtension || (file.type === '' && fileExtension)) : // Camera files might have empty MIME type
        (isValidMimeType || isValidExtension); // Gallery files should have proper validation
      
      if (!isValid) {
        console.warn("Invalid file type selected:", { 
          type: file.type, 
          extension: fileExtension,
          isLikelyFromCamera,
          fileName: file.name
        });
        
        // Reset the input
        e.target.value = '';
        return;
      }
      
      // Android compatibility: Validate file size (max 10MB for mobile)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        console.warn("File too large:", file.size);
        
        e.target.value = '';
        return;
      }

      // Process the image with compression if enabled
      processImage(file);
    } else {
      setSelectedImage(null);
      setImagePreview(null);
      setCompressionResult(undefined);
      setCompressionError(undefined);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="image">Image (Optional)</Label>
        {!selectedImage && (
          <div className="flex items-center gap-2">
            <Label htmlFor="auto-compress" className="text-sm">Auto compress</Label>
            <Switch
              id="auto-compress"
              checked={autoCompress}
              onCheckedChange={setAutoCompress}
            />
          </div>
        )}
      </div>

      {showOptions && !selectedImage ? (
        <ImageUploadOptions
          onGallerySelect={handleGallerySelect}
          onCameraSelect={handleCameraSelect}
          onEitherSelect={handleEitherSelect}
          disabled={isCompressing}
        />
      ) : !selectedImage ? (
        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowOptions(true)}
            className="w-full"
          >
            <Settings className="h-4 w-4 mr-2" />
            Choose Image Source
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Compression Status */}
          <ImageCompressionStatus
            isCompressing={isCompressing}
            compressionResult={compressionResult}
            error={compressionError}
          />

          {/* Image Preview */}
          {imagePreview && (
            <div className="relative inline-block">
              <img 
                src={imagePreview} 
                alt="Selected image preview" 
                className="w-32 h-32 object-cover rounded-lg border border-border"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={removeImage}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                disabled={isCompressing}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
          
          {/* File Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <ImageIcon className="h-4 w-4" />
            <span>
              {selectedImage.name || 'Camera image'} 
              ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
            </span>
          </div>
          
          {/* Change Image Button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowOptions(true)}
            disabled={isCompressing}
          >
            Change Image
          </Button>
        </div>
      )}

      {/* Hidden Inputs for Different Sources */}
      <Input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <Input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <Input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default ImageUploadField;
